import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Fingerprint,
  ClipboardList,
  CalendarOff,
  Menu,
  LogOut,
} from "lucide-react";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";

const TABS = [
  { to: "/me", label: "Home", icon: LayoutDashboard, end: true, testid: "me-tab-home" },
  { to: "/me/attendance", label: "Attendance", icon: Fingerprint, testid: "me-tab-attendance" },
  { to: "/me/tasks", label: "Tasks", icon: ClipboardList, testid: "me-tab-tasks", badgeKey: "openTasks" },
  { to: "/me/leaves", label: "Leaves", icon: CalendarOff, testid: "me-tab-leaves" },
  { to: "/me/more", label: "More", icon: Menu, testid: "me-tab-more" },
];

function MeInner({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openTasks, setOpenTasks] = useState(0);

  // Periodically refresh task badge count
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/delegation/tasks", {
          params: { mode: "assignee", status: "open", limit: 100 },
        });
        if (!cancelled) setOpenTasks((data || []).length);
      } catch { /* non-blocking */ }
    };
    tick();
    const t = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const badges = { openTasks };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="me-shell">
      {/* Top Bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-red-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl brand-bg text-white flex items-center justify-center font-bold text-sm overflow-hidden flex-shrink-0">
            {user?.profile_photo_url
              ? <img src={`/api/files/${user.profile_photo_url}`} alt="" className="h-full w-full object-cover" />
              : (user?.name?.[0]?.toUpperCase() || "U")}
          </div>
          <div className="leading-tight min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">My Portal</div>
            <div className="text-sm font-semibold text-slate-900 truncate">
              {user?.name || "Employee"}
            </div>
          </div>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          className="h-9 w-9 rounded-full ring-1 ring-red-100 hover:bg-red-50 flex items-center justify-center transition flex-shrink-0"
          data-testid="me-logout-btn"
          title="Logout"
        >
          <LogOut className="h-4 w-4 text-slate-600" />
        </button>
      </header>

      {/* Main */}
      <main className="flex-1 px-4 pt-4 pb-24 max-w-2xl w-full mx-auto">
        {children}
      </main>

      {/* PWA Install prompt */}
      <InstallPrompt />

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-red-100 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]">
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          {TABS.map((t) => {
            const badge = t.badgeKey ? badges[t.badgeKey] : 0;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={t.testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-3 gap-1 text-[10px] font-medium transition ${
                    isActive ? "brand-text" : "text-slate-500"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`relative h-9 w-9 rounded-xl flex items-center justify-center transition ${
                        isActive ? "bg-red-50 ring-2 ring-red-200" : "bg-transparent"
                      }`}
                    >
                      <t.icon className="h-5 w-5" />
                      {badge > 0 && (
                        <span
                          data-testid={`${t.testid}-badge`}
                          className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1"
                        >
                          {badge > 9 ? "9+" : badge}
                        </span>
                      )}
                    </div>
                    {t.label}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default function EmployeeShell({ children }) {
  return (
    <ProtectedRoute portalOnly>
      <MeInner>{children}</MeInner>
    </ProtectedRoute>
  );
}
